import type { TFunction } from 'i18next';

/**
 * Structured validation error response from the API.
 * Matches the backend `ValidationErrorResponse` DTO.
 *
 * Once the API types are regenerated via `npm run update-api`,
 * this interface will also be available as a generated type in `data-contracts.ts`.
 */
export interface ValidationErrorResponse {
    /** Unique string code identifying the validation rule that failed. */
    errorCode: string;
    /** Human-readable fallback message in English. */
    message: string;
    /** Parameters for i18n interpolation. */
    params: Record<string, string | number>;
}

/**
 * Type guard to check if an error response body is a `ValidationErrorResponse`.
 */
export function isValidationError(body: unknown): body is ValidationErrorResponse {
    return (
        typeof body === 'object' &&
        body !== null &&
        'errorCode' in body &&
        'message' in body &&
        'params' in body &&
        typeof (body as ValidationErrorResponse).errorCode === 'string' &&
        typeof (body as ValidationErrorResponse).message === 'string'
    );
}

/**
 * Extracts a `ValidationErrorResponse` from a caught API error.
 *
 * Works with the generated `swagger-typescript-api` HttpClient which puts
 * the parsed response body on `error.error` for non-2xx responses,
 * and also with axios-style `error.response.data`.
 *
 * @returns The structured validation error, or `null` if the error is not a validation error.
 */
export function extractValidationError(error: unknown): ValidationErrorResponse | null {
    if (!error || typeof error !== 'object') return null;

    // swagger-typescript-api HttpClient: error.error contains the parsed response body
    const swaggerError = (error as { error?: unknown }).error;
    if (isValidationError(swaggerError)) return swaggerError;

    // axios-style: error.response.data
    const axiosData = (error as { response?: { data?: unknown } }).response?.data;
    if (isValidationError(axiosData)) return axiosData;

    // Direct object (e.g., already extracted)
    if (isValidationError(error)) return error as ValidationErrorResponse;

    return null;
}

/**
 * Translates a validation error using the i18n framework.
 *
 * Looks up the translation key `timesheet:validation.{errorCode}` and interpolates
 * the error's params. Falls back to the English `message` if no translation is found.
 *
 * @example
 * ```ts
 * const { t } = useTranslation('timesheet');
 *
 * try {
 *     await api.registerTimeHours(entries);
 * } catch (error) {
 *     const validationError = extractValidationError(error);
 *     if (validationError) {
 *         const message = translateValidationError(t, validationError);
 *         showError(message);
 *     }
 * }
 * ```
 */
export function translateValidationError(t: TFunction, error: ValidationErrorResponse): string {
    const key = `validation.${error.errorCode}`;
    const translated = t(key, error.params);

    // i18next returns the key itself when no translation is found
    if (translated === key) {
        return error.message;
    }

    return translated;
}
